import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';
import { moneySchema } from '../money';

/**
 * Sperrlager (Modul Logistik): Artikel OHNE Stammdaten-Bezug (Restmaterial aus
 * Projekten etc.), verwaltet nach Stellplatz und mit Fotos. Contracts für die REST-API
 * und den Import aus dem Alt-System (base44-JSON). Frontend und Backend teilen sie.
 */

// ── Foto ────────────────────────────────────────────────────────────────────

/** Ein Foto zu einem Sperrlager-Artikel — lokal gespeichert (`hasFile`) oder externer Link. */
export const blockedStockPhotoResponseSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  note: z.string().nullable(),
  /** true, wenn die Bytes lokal im Datei-Backend liegen (`storageKey`). */
  hasFile: z.boolean(),
  /** Externer Link als Fallback, falls das Bild nicht lokal gespeichert wurde. */
  sourceUrl: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type BlockedStockPhotoResponse = z.infer<typeof blockedStockPhotoResponseSchema>;

// ── Artikel ──────────────────────────────────────────────────────────────────

export const blockedStockItemResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  manufacturer: z.string().nullable(),
  category: z.string().nullable(),
  storageLocation: z.string().nullable(),
  articleNumber: z.string().nullable(),
  abbreviation: z.string().nullable(),
  material: z.string().nullable(),
  quantity: z.number(),
  unit: z.string().nullable(),
  description: z.string(),
  dimension: z.string().nullable(),
  note: z.string().nullable(),
  /** Alt-Feld (Freitext, nur Import) — im manuellen Anlegen/Bearbeiten nicht mehr gesetzt. */
  valuation: z.string().nullable(),
  /** Bewertung €/Stück (ADR: nur `blockedstock.viewvaluation`) — serverseitig auf null redigiert ohne Recht. */
  valuationAmount: moneySchema.nullable(),
  externalId: z.string().nullable(),
  photos: z.array(blockedStockPhotoResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type BlockedStockItemResponse = z.infer<typeof blockedStockItemResponseSchema>;

/** Frei-Text-Feld: trimmt, leer wird zu null, akzeptiert null/undefined. */
const optionalText = z.string().trim().max(2000).nullish();

/** Sperrlager-Artikel anlegen — nur `description` ist Pflicht (§6: sprechende Bezeichnung). */
export const createBlockedStockItemSchema = z.object({
  description: z.string().trim().min(1, 'Beschreibung ist erforderlich.').max(2000),
  manufacturer: optionalText,
  category: optionalText,
  storageLocation: optionalText,
  articleNumber: optionalText,
  abbreviation: optionalText,
  material: optionalText,
  quantity: z.number().min(0).max(1_000_000_000).default(0),
  unit: z.string().trim().max(100).nullish(),
  dimension: optionalText,
  note: optionalText,
  /** Nur wirksam mit `blockedstock.viewvaluation` — sonst serverseitig ignoriert. */
  valuationAmount: moneySchema.nullable().optional(),
});
export type CreateBlockedStockItemInput = z.infer<typeof createBlockedStockItemSchema>;

/** Sperrlager-Artikel ändern — alle Felder optional. */
export const updateBlockedStockItemSchema = createBlockedStockItemSchema.partial();
export type UpdateBlockedStockItemInput = z.infer<typeof updateBlockedStockItemSchema>;

/** Listen-Query: Standard-Pagination + Filter auf Kategorie/Stellplatz/Hersteller. */
export const listBlockedStockQuerySchema = listQuerySchema.extend({
  category: z.string().trim().optional(),
  storageLocation: z.string().trim().optional(),
  manufacturer: z.string().trim().optional(),
});
export type ListBlockedStockQuery = z.infer<typeof listBlockedStockQuerySchema>;

/**
 * Verfügbare Filterwerte (distinct, Toolbar-Dropdowns + Autocomplete-Vorschläge
 * beim Anlegen) + Gesamtwert (Menge × Bewertung, alle Artikel). `totalValuationAmount`
 * ist `null` ohne `blockedstock.viewvaluation` oder ohne bewertete Artikel.
 */
export const blockedStockFacetsResponseSchema = z.object({
  categories: z.array(z.string()),
  storageLocations: z.array(z.string()),
  manufacturers: z.array(z.string()),
  materials: z.array(z.string()),
  descriptions: z.array(z.string()),
  totalValuationAmount: moneySchema.nullable(),
});
export type BlockedStockFacetsResponse = z.infer<typeof blockedStockFacetsResponseSchema>;

/** Foto-Metadaten beim Upload (Datei kommt als Rohdaten im Body). */
export const uploadBlockedStockPhotoSchema = z.object({
  note: z.string().trim().max(2000).nullish(),
});
export type UploadBlockedStockPhotoInput = z.infer<typeof uploadBlockedStockPhotoSchema>;

// ── Import (Alt-System-JSON, base44) ─────────────────────────────────────────

/**
 * Tolerantes Text-Feld für den Import: null/undefined → undefined, Zahlen werden zu
 * Strings, sonst muss es ein String sein. So verkraftet der Import die uneinheitlichen
 * Typen des Alt-Exports, ohne eine ganze Zeile zu verwerfen.
 */
const looseText = z.preprocess((value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return value;
}, z.string().optional());

/** Tolerante Zahl: akzeptiert Zahl oder Zahl-String (auch mit Komma), sonst undefined. */
const looseNumber = z.preprocess((value) => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.').trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}, z.number().optional());

/** Tolerante String-Liste (Foto-URLs): filtert Nicht-Strings heraus. */
const looseStringArray = z.preprocess((value) => {
  if (!Array.isArray(value)) return undefined;
  return value.filter((entry): entry is string => typeof entry === 'string');
}, z.array(z.string()).optional());

/** Eine Zeile des Alt-System-Exports (base44). Alle Felder tolerant/optional. */
export const blockedStockImportRowSchema = z
  .object({
    hersteller: looseText,
    kategorie: looseText,
    stellplatz: looseText,
    artikelnummer: looseText,
    kuerzel: looseText,
    einheit: looseText,
    material: looseText,
    anzahl: looseNumber,
    artikelbeschreibung: looseText,
    bemerkung: looseText,
    abmessung: looseText,
    bewertung: looseText,
    fotos: looseStringArray,
    id: looseText,
  })
  .passthrough();
export type BlockedStockImportRow = z.infer<typeof blockedStockImportRowSchema>;

/** Obergrenze je Import-Aufruf — schützt vor versehentlichen Riesen-Uploads. */
const MAX_IMPORT_ROWS = 5000;

export const blockedStockImportSchema = z.object({
  rows: z.array(blockedStockImportRowSchema).min(1).max(MAX_IMPORT_ROWS),
});
export type BlockedStockImportInput = z.infer<typeof blockedStockImportSchema>;

/** Ein Fehler-Eintrag je fehlgeschlagener Import-Zeile. */
export const blockedStockImportErrorSchema = z.object({
  index: z.number().int(),
  value: z.string(),
  message: z.string(),
});
export type BlockedStockImportError = z.infer<typeof blockedStockImportErrorSchema>;

export const blockedStockImportResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  photosStored: z.number().int().nonnegative(),
  photosLinked: z.number().int().nonnegative(),
  /**
   * Download versucht (auch nach Retry) und gescheitert — Speicher war
   * konfiguriert, die URL war aber nicht (mehr) erreichbar. Getrennt von
   * `photosLinked` (dort war kein Speicher konfiguriert, gar kein Versuch nötig),
   * weil beide sonst nicht auseinanderzuhalten sind: „141 verlinkt" bedeutet sonst
   * einmal „Speicher fehlt" und einmal „141 tote URLs" — sehr unterschiedliche
   * Dringlichkeit.
   */
  photosDownloadFailed: z.number().int().nonnegative(),
  photosFailed: z.number().int().nonnegative(),
  /**
   * Angelegte Artikel OHNE Stellplatz (Alt-System erlaubte das) — kein Fehler,
   * aber ein Hinweis: diese Artikel müssen nach dem Import noch physisch
   * einem Lagerplatz zugeordnet und im System nachgetragen werden.
   */
  createdWithoutStorageLocation: z.number().int().nonnegative(),
  errors: z.array(blockedStockImportErrorSchema),
});
export type BlockedStockImportResult = z.infer<typeof blockedStockImportResultSchema>;
